import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Insert, type Row } from '@/lib/supabase'
import { daysUntil, today, type IsoDate } from '@/lib/dates'

export type Task = Row<'tasks'>

export interface TaskWithRelations extends Task {
  assignee: Pick<Row<'staff'>, 'id' | 'full_name'> | null
  job: { id: string; job_number: number; event_date: IsoDate } | null
}

const TASK_SELECT = `
  *,
  assignee:staff!tasks_assigned_to_fkey ( id, full_name ),
  job:jobs!tasks_job_id_fkey ( id, job_number, event_date )
` as const

export const taskKeys = {
  list: (scope: string) => ['tasks', scope] as const,
  forJob: (jobId: string) => ['tasks', 'job', jobId] as const,
}

/**
 * Open tasks, soonest first, with undated ones last.
 *
 * `nullsFirst: false` matters: a task with no due date is a someday task and
 * belongs at the bottom, not at the top where Postgres would otherwise put it.
 */
export function useTasks(scope: 'open' | 'done' = 'open') {
  return useQuery({
    queryKey: taskKeys.list(scope),
    queryFn: async (): Promise<TaskWithRelations[]> => {
      let query = supabase.from('tasks').select(TASK_SELECT)

      if (scope === 'open') {
        query = query.is('completed_at', null).order('due_date', { ascending: true, nullsFirst: false })
      } else {
        query = query.not('completed_at', 'is', null).order('completed_at', { ascending: false }).limit(100)
      }

      const { data, error } = await query
      if (error) throw error
      return data as unknown as TaskWithRelations[]
    },
  })
}

export function useJobTasks(jobId: string | undefined) {
  return useQuery({
    queryKey: taskKeys.forJob(jobId ?? ''),
    enabled: Boolean(jobId),
    queryFn: async (): Promise<TaskWithRelations[]> => {
      const { data, error } = await supabase
        .from('tasks')
        .select(TASK_SELECT)
        .eq('job_id', jobId ?? '')
        .order('completed_at', { ascending: true, nullsFirst: true })
        .order('due_date', { ascending: true, nullsFirst: false })

      if (error) throw error
      return data as unknown as TaskWithRelations[]
    },
  })
}

export function useCreateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: Insert<'tasks'>) => {
      const { error } = await supabase.from('tasks').insert(values)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
    onError: (error) => {
      toast.error('Could not add the task', { description: describeError(error) })
    },
  })
}

/**
 * Ticking a task off. Optimistic, because she will tick five in a row and a
 * spinner on each one would make that feel like work.
 */
export function useToggleTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase
        .from('tasks')
        .update({ completed_at: done ? new Date().toISOString() : null })
        .eq('id', id)

      if (error) throw error
    },
    onMutate: async ({ id, done }) => {
      const keys = [taskKeys.list('open'), taskKeys.list('done')]
      await Promise.all(keys.map((key) => queryClient.cancelQueries({ queryKey: key })))

      const snapshots = keys.map((key) => [key, queryClient.getQueryData<TaskWithRelations[]>(key)] as const)

      for (const [key] of snapshots) {
        queryClient.setQueryData<TaskWithRelations[]>(key, (current) =>
          current?.map((task) =>
            task.id === id ? { ...task, completed_at: done ? new Date().toISOString() : null } : task,
          ),
        )
      }

      return { snapshots }
    },
    onError: (error, _variables, context) => {
      for (const [key, value] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, value)
      }
      toast.error('Could not update the task', { description: describeError(error) })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Task> }) => {
      const { error } = await supabase.from('tasks').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
    onError: (error) => {
      toast.error('Could not save the task', { description: describeError(error) })
    },
  })
}

export function useDeleteTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tasks').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
    onError: (error) => {
      toast.error('Could not delete the task', { description: describeError(error) })
    },
  })
}

export type TaskUrgency = 'overdue' | 'today' | 'soon' | 'later' | 'someday'

export function taskUrgency(task: Task, from: IsoDate = today()): TaskUrgency {
  if (!task.due_date) return 'someday'

  const days = daysUntil(task.due_date, from)
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  if (days <= 7) return 'soon'
  return 'later'
}
